import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IconButton from 'components/common/IconButton';
import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import 'react-loading-skeleton/dist/skeleton.css';

import DatosFiscalesReceptor from './DatosFiscalesReceptor';

import PreferenciasComplementoPago from './PreferenciasComplementoPago';

import { useToast } from 'context/providers/ToastProvider';
import useToken from 'hooks/useToken';
import InformacionGeneralComplemento from './InformacionGeneralComplemento';
import DocumentoRelacionado from './DocumentoRelacionado';

import { useParams } from 'react-router-dom';
import { ContabilidadLayout } from 'akasia-components/Contabilidad/common/ContabilidadLayout';
import { ContabilidadContext } from 'akasia-components/Contabilidad/common/ContabilidadContext';
import { useContext } from 'react';
import SelectFacturaPPDModal from './SelectFacturaPPDModal';

const FormularioComplementoPagoContent = () => {
    const { idOrden, idPago } = useParams();

    // States for SelectFacturaModal and loaded data
    const [selectedFacturas, setSelectedFacturas] = useState([]);
    const [showSelectFacturaModal, setShowSelectFacturaModal] = useState(false);


    const { register, setValue, getValues } = useForm();
    const { showSuccessToast, showErrorToast } = useToast();
    const { token, setToken } = useToken();
    const navigate = useNavigate();

    //estado para cuando se envia a timbrar
    const [facturandoTimbrando, setFacturandoTimbrando] = useState(false);

    const { selectedCompany } = useContext(ContabilidadContext);
    const idEmisorSeleccionado = selectedCompany?.idDatoFiscal || "";
    const rfcSeleccionado = selectedCompany?.vRFC || "";

    // Estado para almacenar los impuestos de las facturas (diccionario indexado por UUID)
    const [impuestosFacturas, setImpuestosFacturas] = useState({});

    // Estados para saldo de timbres
    const [cargandoSaldo, setCargandoSaldo] = useState(false);
    const [saldoTimbres, setSaldoTimbres] = useState(null);

    const [selectedValuesDDL, setSelectedValuesDDL] = useState({
        vRFC: "",
        vCodigoRegimen: "",
        vMoneda: "",
        vFormaPago: "",
        vMetodoPagoDR: "",
        vObjetoImpDR: "",
        vSerie: "",
        vPais: ""
    });

    const now = new Date();
    const minDate = new Date(now.getTime() - 70 * 60 * 60 * 1000);
    const [errors, setErrors] = useState({});
    const [isForeignRFC, setIsForeignRFC] = useState(false);

    // Inicializar fechas por defecto a la fecha actual si no tienen valor
    useEffect(() => {
        const formatLocalDateTime = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        };

        const nowFormatted = formatLocalDateTime(new Date());

        if (!getValues('dFechaEmision')) {
            setValue('dFechaEmision', nowFormatted);
        }
        if (!getValues('dFechaPago')) {
            setValue('dFechaPago', nowFormatted);
            setSelectedValuesDDL(prev => ({ ...prev, dFechaPago: nowFormatted }));
        }
    }, [setValue, getValues]);

    useEffect(() => {
        if (idOrden) {
            obtenerDatosInit(idOrden, idPago);
        }
    }, [idOrden, idPago]);

    useEffect(() => {
        ObtenerSaldoTimbres(); // Obtener saldo al cargar el componente
    }, []);

    /**
    * Obtiene el saldo de timbres disponibles desde Prodigia
    */
    const ObtenerSaldoTimbres = async () => {
        setCargandoSaldo(true);

        try {
            const response = await fetch("/api/contabilidad/facturas/FormularioFactura/ObtenerSaldoTimbres", {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();

                if (data.iResponseType === 1) {
                    setSaldoTimbres(data.saldo);
                } else {
                    console.warn("No se pudo obtener el saldo de timbres:", data.vMessage);
                    setSaldoTimbres(null);
                    showErrorToast(data.vMessage || "No se pudo obtener el saldo de timbres");
                }
            } else {
                console.error("Error al obtener saldo de timbres:", response.status);
                setSaldoTimbres(null);
                
                try {
                    const errorData = await response.json();
                    showErrorToast(errorData.vMessage || `Error al obtener saldo de timbres: ${response.status}`);
                } catch (e) {
                    showErrorToast(`Error al obtener saldo de timbres: ${response.status}`);
                }

                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            console.error("Error en la petición de saldo de timbres:", error);
            setSaldoTimbres(null);
            showErrorToast('Error en la petición de saldo de timbres: ' + error.message);
        } finally {
            setCargandoSaldo(false);
        }
    }

    const obtenerDatosInit = async (ordenId, pagoId) => {
        try {
            const resOrden = await fetch("/api/contabilidad/facturas/FormularioFactura/ObtenerDetalleOrden", {
                method: 'POST',
                headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
                body: JSON.stringify({ idOrden: ordenId })
            });
            if (resOrden.ok) {
                const data = await resOrden.json();
                if (data.iResponseType === 1) {
                    const detallesObj = JSON.parse(data.detalles);
                    const detalles = detallesObj.table2[0];

                    const rfcOrden = detalles.vRfc || '';
                    setValue('vRFC', rfcOrden);
                    setValue('vRazonSocial', detalles.vRazonSocial || '');
                    setValue('vCP', detalles.vCp || '');
                    setValue('vEmail', detalles.vEmailFacturacion || '');
                    setValue('vNumRegTrib', detalles.vNumRegTrib || '');
                    setSelectedValuesDDL(prev => ({
                        ...prev,
                        vRFC: rfcOrden,
                        vCodigoRegimen: detalles.vCodigoRegimen || '',
                        vPais: detalles.vPaisFacturacion || '',
                        vSerie: prev.vSerie || ''
                    }));
                    if (rfcOrden === 'XEXX010101000') setIsForeignRFC(true);
                }
            }

            // Fetch pagos si hay idPago
            if (pagoId) {
                const resPagos = await fetch("/api/orden/ObtenerPagosOrden/?id=" + ordenId, {
                    method: 'GET',
                    headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' }
                });
                if (resPagos.ok) {
                    const dataPagos = await resPagos.json();
                    const pagos = dataPagos["table3"] || [];
                    const pagoObj = pagos.find(p => p.idPago === parseInt(pagoId));
                    if (pagoObj) {
                        setValue('dImportePago', pagoObj.dImportePago);
                        // Se pueden setear más campos del pago aquí si se requieren
                    }
                }
            }

        } catch (e) {
            console.error("Error al obtener datos init: ", e);
        }
    };

    /**
     * Abrir modal de selección de facturas y buscar facturas PPD por RFC
     */
    const handleOpenSelectFactura = () => {
        setShowSelectFacturaModal(true);
    };

    // Efecto para obtener los impuestos de facturas recién seleccionadas
    useEffect(() => {
        const cargarImpuestosFacturas = async () => {
            if (selectedFacturas.length === 0) return;

            const nuevosImpuestos = { ...impuestosFacturas };
            let hasChanges = false;

            for (const factura of selectedFacturas) {
                const uuid = factura.vUUIDFactura;
                if (!uuid || nuevosImpuestos[uuid]) continue; // Ya los tenemos

                try {
                    const response = await fetch(`/api/contabilidad/facturas/FormularioFactura/ObtenerImpuestosFactura?uuid=${uuid}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': 'Bearer ' + token,
                            'Content-Type': 'application/json'
                        }
                    });

                    if (response.ok) {
                        const facturaData = await response.json();

                        if (facturaData.iResponseType === 1) {
                            const traslados = facturaData.impuestosTrasladados || [];
                            const retenciones = facturaData.impuestosRetenidos || [];

                            const trasladosDR = traslados.map(traslado => ({
                                BaseOriginal: parseFloat(traslado.Base || "0"),
                                ImpuestoDR: traslado.TipoImpuesto || traslado.Impuesto || "002",
                                TipoFactorDR: traslado.TipoFactor || "Tasa",
                                TasaOCuotaDR: traslado.TasaOCuota || "0.000000",
                                ImporteOriginal: parseFloat(traslado.Importe || "0")
                            }));

                            const retencionesDR = retenciones.map(retencion => ({
                                BaseOriginal: parseFloat(retencion.Base || "0"),
                                ImpuestoDR: retencion.TipoImpuesto || retencion.Impuesto || "002",
                                TipoFactorDR: retencion.TipoFactor || "Tasa",
                                TasaOCuotaDR: retencion.TasaOCuota || "0.000000",
                                ImporteOriginal: parseFloat(retencion.Monto || retencion.Importe || "0")
                            }));

                            if (trasladosDR.length > 0 || retencionesDR.length > 0) {
                                nuevosImpuestos[uuid] = {
                                    TrasladosDR: trasladosDR.length > 0 ? trasladosDR : null,
                                    RetencionesDR: retencionesDR.length > 0 ? retencionesDR : null,
                                    TotalFactura: parseFloat(factura.dTotal || "0")
                                };
                                hasChanges = true;
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error al cargar impuestos de la factura ' + uuid + ':', error);
                }
            }

            if (hasChanges) {
                setImpuestosFacturas(nuevosImpuestos);
            }
        };

        cargarImpuestosFacturas();
    }, [selectedFacturas]);

    /**
     * Cambios de RFC
     */
    const handleRFCChange = (rfc) => {
        rfc = rfc.toUpperCase().trim();

        setValue("vRFC", rfc, { shouldValidate: true });

        // Actualizar selectedValues con el nuevo RFC para que DatosFiscalesReceptor lo detecte
        setSelectedValuesDDL((prev) => ({ ...prev, vRFC: rfc }));

        // Detectar si es RFC extranjero
        if (rfc === 'XEXX010101000') {
            setIsForeignRFC(true);
        } else {
            setIsForeignRFC(false);
            // Limpiar el valor del país si no es RFC extranjero
            setSelectedValuesDDL((prev) => ({ ...prev, vPais: '' }));
        }
    }

    /**
     * Limpia Errores 
     */
    const clearError = (fieldName) => {
        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[fieldName];
            return newErrors;
        });
    }

    const validarDatosComplemento = (data) => {
        let newErrors = {};

        // Validar emisor seleccionado
        if (!idEmisorSeleccionado) {
            newErrors.idEmisor = "Debe seleccionar un Emisor";
        }
        //  alert(data["vFormaPago"]);
        // Lista de campos obligatorios

        const camposObligatoriosFormulario = [
            { key: 'vRFC', mensaje: 'El RFC es requerido' },
            { key: 'vRazonSocial', mensaje: 'La Razón Social es requerida' },
            { key: 'vCP', mensaje: 'El Código Postal es requerido' },
            { key: 'vCodigoRegimen', mensaje: 'El Régimen Fiscal es requerido' },
            { key: 'dFechaPago', mensaje: 'El campo Fecha del Pago es requerido' },
            { key: 'vFormaPago', mensaje: 'El campo Forma de Pago es requerido' },
            { key: 'dMonto', mensaje: 'El campo Monto es requerido' }
        ];

        // Verificar campos obligatorios generales
        camposObligatoriosFormulario.forEach(({ key, mensaje }) => {
            if (!data[key]) {
                newErrors[key] = mensaje;
            }
        });

        // Validar documentos relacionados
        if (!data.doctosRelacionados || data.doctosRelacionados.length === 0) {
            newErrors.doctosRelacionados = "Debe agregar al menos un documento relacionado";
        } else {
            data.doctosRelacionados.forEach((doc, index) => {
                if (!doc.dSaldoAnterior) newErrors[`doctosRelacionados.${index}.dSaldoAnterior`] = 'Requerido';
                if (parseFloat(doc.dSaldoAnterior) < 0) newErrors[`doctosRelacionados.${index}.dSaldoAnterior`] = 'Debe ser positivo';

                if (doc.dSaldoInsoluto === undefined || doc.dSaldoInsoluto === '') newErrors[`doctosRelacionados.${index}.dSaldoInsoluto`] = 'Requerido';
                if (parseFloat(doc.dSaldoInsoluto) < 0) newErrors[`doctosRelacionados.${index}.dSaldoInsoluto`] = 'Debe ser positivo';

                if (!doc.dImportePago) newErrors[`doctosRelacionados.${index}.dImportePago`] = 'Requerido';
                if (parseFloat(doc.dImportePago) <= 0) newErrors[`doctosRelacionados.${index}.dImportePago`] = 'Debe ser mayor a cero';

                if (!doc.iNumeroParcialidad) {
                    newErrors[`doctosRelacionados.${index}.iNumeroParcialidad`] = 'Requerido';
                } else if (parseInt(doc.iNumeroParcialidad) <= 0 || !Number.isInteger(parseFloat(doc.iNumeroParcialidad))) {
                    newErrors[`doctosRelacionados.${index}.iNumeroParcialidad`] = 'Debe ser entero positivo';
                }

                if (!doc.vObjetoImpDR) newErrors[`doctosRelacionados.${index}.vObjetoImpDR`] = 'Requerido';
            });
        }

        // Validar tipo de cambio solo si la moneda NO es MXN
        if (data.vMoneda && data.vMoneda !== "MXN") {
            if (!data.dTipoCambio || data.dTipoCambio <= 0) {
                newErrors.dTipoCambio = "El tipo de cambio es obligatorio para monedas distintas a MXN";
            }
        }

        // Validar país si es RFC extranjero
        if (isForeignRFC && !data.vPais) {
            newErrors.vPais = "El campo País es requerido para RFC extranjero";
        }

        // Validación específica para Código Postal (vCP)
        if (data.vCP && !/^\d{5}$/.test(data.vCP)) {
            newErrors.vCP = "El código postal debe tener 5 dígitos numéricos";
        }

        // Validación del email
        if (data.vEmail && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(data.vEmail)) {
            newErrors.vEmail = "Formato de correo no válido";
        }

        if (data.dMonto && parseFloat(data.dMonto) <= 0) {
            newErrors.dMonto = "El monto debe ser mayor a cero";
        }

        return newErrors;
    }

    const handleSubmit = (e = null) => {
        if (e) e.preventDefault();

        // Obtén los valores del formulario registrados con react-hook-form
        const formData = getValues();
        // Combina los valores seleccionados manualmente con los del formulario
        const data = { ...selectedValuesDDL, ...formData };


        // Validar antes de enviar
        const errores = validarDatosComplemento(data);
        if (Object.keys(errores).length > 0) {
            setErrors(errores); // Establece los errores en el estado
            const errorMessages = Object.values(errores).join(', ');
            showErrorToast(`Errores de validación: ${errorMessages}`);
            return; // Detiene la ejecución si hay errores
        }

        // Si no hay errores, procede con el registro
        registrarComplementoPago(data);
    }

    /**
    * Registrar complemento de pago
    */
    const registrarComplementoPago = async (data) => {
        setFacturandoTimbrando(true);

        try {
            // Procesar Documentos Relacionados y sus impuestos proporcionales
            const doctosRelacionadosList = [];

            if (data.doctosRelacionados && Array.isArray(data.doctosRelacionados)) {
                data.doctosRelacionados.forEach((docInput, index) => {
                    const facturaObj = selectedFacturas[index];
                    if (!facturaObj) return;

                    const uuid = facturaObj.vUUIDFactura;
                    let impuestosDRProporcionales = null;
                    const impuestosFactura = impuestosFacturas[uuid];

                    const importePagado = parseFloat(docInput.dImportePago || 0);

                    if (impuestosFactura && importePagado > 0 && impuestosFactura.TotalFactura > 0) {
                        const totalFactura = impuestosFactura.TotalFactura;
                        const proporcion = importePagado / totalFactura;

                        impuestosDRProporcionales = {};

                        if (impuestosFactura.TrasladosDR) {
                            impuestosDRProporcionales.TrasladosDR = impuestosFactura.TrasladosDR.map(traslado => ({
                                BaseDR: (traslado.BaseOriginal * proporcion).toFixed(6),
                                ImpuestoDR: traslado.ImpuestoDR,
                                TipoFactorDR: traslado.TipoFactorDR,
                                TasaOCuotaDR: traslado.TasaOCuotaDR,
                                ImporteDR: (traslado.ImporteOriginal * proporcion).toFixed(6)
                            }));
                        }

                        if (impuestosFactura.RetencionesDR) {
                            impuestosDRProporcionales.RetencionesDR = impuestosFactura.RetencionesDR.map(retencion => ({
                                BaseDR: (retencion.BaseOriginal * proporcion).toFixed(6),
                                ImpuestoDR: retencion.ImpuestoDR,
                                TipoFactorDR: retencion.TipoFactorDR,
                                TasaOCuotaDR: retencion.TasaOCuotaDR,
                                ImporteDR: (retencion.ImporteOriginal * proporcion).toFixed(6)
                            }));
                        }
                    }

                    doctosRelacionadosList.push({
                        IdDocumento: facturaObj.vUUIDFactura,
                        Serie: facturaObj.vSerie,
                        Folio: facturaObj.vFolio,
                        MonedaDR: facturaObj.vMoneda,
                        EquivalenciaDR: docInput.dEquivalenciaDR ? parseFloat(docInput.dEquivalenciaDR) : 1,
                        NumParcialidad: parseInt(docInput.iNumeroParcialidad || 1),
                        ImpSaldoAnt: parseFloat(docInput.dSaldoAnterior || 0),
                        ImpPagado: parseFloat(docInput.dImportePago || 0),
                        ImpSaldoInsoluto: parseFloat(docInput.dSaldoInsoluto || 0),
                        ObjetoImpDR: docInput.vObjetoImpDR || '01',
                        ImpuestosDR: impuestosDRProporcionales
                    });
                });
            }

            // Estructurar los datos en el formato requerido por el backend
            const payload = {
                data: {
                    // Datos del Comprobante
                    Comprobante: {
                        Serie: data.vSerie,
                        Folio: data.vFolio,
                        FechaEmision: data.dFechaEmision
                    },

                    // Datos del Receptor
                    Receptor: {
                        Rfc: data.vRFC,
                        Nombre: data.vRazonSocial,
                        DomicilioFiscalReceptor: data.vCP,
                        RegimenFiscalReceptor: data.vCodigoRegimen,
                        Email: data.vEmail || undefined,
                        ResidenciaFiscal: isForeignRFC ? data.vPais : undefined,
                        NumRegTrib: isForeignRFC ? data.vNumRegTrib : undefined
                    },

                    // Datos del Pago
                    Pago: {
                        FechaPago: data.dFechaPago,
                        FormaDePagoP: data.vFormaPago,
                        MonedaP: data.vMoneda,
                        Monto: parseFloat(data.dMonto),
                        TipoCambioP: data.vMoneda !== "MXN" ? parseFloat(data.dTipoCambio) : 1,
                        NumOperacion: data.vNumOperacion || undefined
                    },

                    // Datos del Documento Relacionado
                    DoctoRelacionado: doctosRelacionadosList
                },
                idEmisor: parseInt(idEmisorSeleccionado),
                IdPago: idPago // Agregar idPago para vincular con tblPago
            };

            console.log('Payload con impuestos proporcionales:', JSON.stringify(payload, null, 2));

            const response = await fetch("/api/contabilidad/Facturacion/registrarComplementoPago", {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const responseData = await response.json();

                if (responseData.iResponseType === 1) {
                    showSuccessToast(responseData.vMessage || 'Complemento de pago generado exitosamente');
                    if (responseData.redirectUrl) {
                        navigate(responseData.redirectUrl);
                    } else {
                        navigate(-1);
                    }

                    // Redirigir a la URL proporcionada por el backend
                    if (responseData.redirectUrl) {
                        navigate(responseData.redirectUrl);
                    }
                } else {
                    showErrorToast(responseData.vMessage || "Error al procesar el complemento de pago");
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                showErrorToast(errorData.vMessage || `Error al procesar el complemento de pago: ${response.status}`);
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            showErrorToast('Hubo un problema con la petición: ' + error.message);
        } finally {
            setFacturandoTimbrando(false);
        }
    };

    /**
     * Fecha valores e icono 
     */
    const GreetingsDateInput = forwardRef(({ value, onClick }, ref) => (
        <div className="position-relative">
            <Form.Control
                size="sm"
                ref={ref}
                onClick={onClick}
                value={value || ""} // Si value es null o undefined, muestra una cadena vacía
                placeholder="Seleccione una fecha" // Muestra el texto cuando no hay valor
                className="ps-4"
                readOnly // Evita edición manual del campo
            />
            <FontAwesomeIcon
                icon="calendar-alt"
                className="text-primary position-absolute top-50 translate-middle-y ms-2"
            />
        </div>
    ));

    GreetingsDateInput.propTypes = {
        value: PropTypes.string,
        onClick: PropTypes.func,
    };

    return (
        <>
            <Card className="mb-1 p-2">
                <Row className="g-2 align-items-sm-center justify-content-between">
                    <Col xs="auto">
                        <IconButton
                            onClick={() => navigate(-1)}
                            variant="falcon-default"
                            size="sm"
                            icon="arrow-left"
                        />
                    </Col>
                    <Col xs="auto" className="text-end">
                        {/* Indicador de saldo de timbres */}
                        {cargandoSaldo ? (
                            <div className="d-flex align-items-center">
                                <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                                <span className="text-muted small">Consultando timbres...</span>
                            </div>
                        ) : saldoTimbres !== null ? (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon="receipt"
                                    className={`me-2 ${saldoTimbres > 30000 ? 'text-success' : saldoTimbres > 20000 ? 'text-warning' : 'text-danger'}`}
                                />
                                <div className="d-flex flex-column align-items-start">
                                    <span className="small text-muted">Timbres disponibles</span>
                                    <span className={`fw-bold ${saldoTimbres > 30000 ? 'text-success' : saldoTimbres > 20000 ? 'text-warning' : 'text-danger'}`}>
                                        {saldoTimbres.toLocaleString()}
                                    </span>
                                </div>
                                <Button
                                    onClick={ObtenerSaldoTimbres}
                                    variant="falcon-default"
                                    size="sm"
                                    className="ms-2"
                                    title="Actualizar saldo"
                                >
                                    <FontAwesomeIcon icon="sync" />
                                </Button>
                            </div>
                        ) : (
                            <div className="d-flex align-items-center text-muted">
                                <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                                <span className="small">No Disponible</span>
                                <Button
                                    onClick={ObtenerSaldoTimbres}
                                    variant="falcon-default"
                                    size="sm"
                                    className="ms-2"
                                    title="Reintentar"
                                >
                                    <FontAwesomeIcon icon="sync" />
                                </Button>
                            </div>
                        )}
                    </Col>
                </Row>
            </Card>

            <Form onSubmit={handleSubmit}>
                <Card className="factura-card mb-1 bg-white shadow-none border">
                    <Card.Body className="factura-card-body">
                        <Row>
                            <Col xl={6} style={{ borderRight: "1px solid #ddd", paddingRight: "15px" }}>


                                {/* Sección: Datos Fiscales */}
                                <DatosFiscalesReceptor
                                    cargandoForm={false}
                                    errors={errors}
                                    clearError={clearError}

                                    // Props de datos fiscales del receptor
                                    register={register}
                                    selectedValues={selectedValuesDDL}
                                    setSelectedValues={setSelectedValuesDDL}
                                    handleRFCChange={handleRFCChange}
                                    setValue={setValue}
                                />

                                <InformacionGeneralComplemento
                                    register={register}
                                    selectedValuesDDL={selectedValuesDDL}
                                    setSelectedValuesDDL={setSelectedValuesDDL}
                                    errors={errors}
                                    clearError={clearError}
                                    setValue={setValue}
                                    now={now}
                                    minDate={minDate}
                                />
                            </Col>
                            {/* Sección: Preferencias de Facturación */}
                            <Col xl={6}>
                                <PreferenciasComplementoPago
                                    register={register}
                                    selectedValuesDDL={selectedValuesDDL}
                                    setSelectedValuesDDL={setSelectedValuesDDL}
                                    errors={errors}
                                    clearError={clearError}
                                    setValue={setValue}
                                    now={now}
                                    minDate={minDate}
                                    idPago={idPago}
                                    rfcSeleccionado={rfcSeleccionado}
                                />
                                <DocumentoRelacionado
                                    register={register}
                                    getValues={getValues}
                                    setValue={setValue}
                                    selectedValuesDDL={selectedValuesDDL}
                                    setSelectedValuesDDL={setSelectedValuesDDL}
                                    errors={errors}
                                    clearError={clearError}
                                    selectedFacturas={selectedFacturas}
                                    setSelectedFacturas={setSelectedFacturas}
                                    impuestosFacturas={impuestosFacturas}
                                    onAddFactura={handleOpenSelectFactura}
                                    hasFacturasPPD={true}
                                />
                            </Col>
                        </Row>

                        <Row className="justify-content-end mt-3">
                            {
                                facturandoTimbrando ?
                                    <>
                                        <Col className="col-auto">
                                            <IconButton
                                                variant="falcon-default"
                                                size="sm"
                                                transform="shrink-2"
                                                className="d-sm-block me-2"
                                                disabled
                                            >
                                                <Spinner className="text-inline" animation="border" size="sm" variant="ligth" />&nbsp; Cargando...
                                            </IconButton>
                                        </Col>
                                    </>
                                    :
                                    <>
                                        <Col className="col-auto d-flex align-items-center">
                                            {/* Botón "Generar Complemento" */}
                                            <IconButton
                                                variant="falcon-default"
                                                size="sm"
                                                icon="check"
                                                transform="shrink-2"
                                                type="submit"
                                                className="me-2"
                                            >
                                                <span className=" ms-1">Generar Complemento</span>
                                            </IconButton>

                                            <IconButton
                                                onClick={() => navigate(-1)}
                                                icon="xmark"
                                                variant="falcon-danger"
                                                size="sm"
                                                transform="shrink-2"
                                            >
                                                <span className="ms-1">Cancelar</span>
                                            </IconButton>
                                        </Col>
                                    </>
                            }
                        </Row>
                    </Card.Body>
                </Card>
            </Form>

            <SelectFacturaPPDModal
                show={showSelectFacturaModal}
                onHide={() => setShowSelectFacturaModal(false)}
                onConfirm={(selectedFactura) => {
                    if (selectedFactura && !selectedFacturas.find(f => f.vUUIDFactura === selectedFactura.vUUIDFactura)) {
                        setSelectedFacturas([...selectedFacturas, selectedFactura]);

                        // Autocompletar datos del receptor si están vacíos
                        if (!getValues('vRFC') && selectedFactura.vRfcReceptor) {
                            handleRFCChange(selectedFactura.vRfcReceptor);
                        }
                        if (!getValues('vRazonSocial') && selectedFactura.vRazonSocialReceptor) {
                            setValue('vRazonSocial', selectedFactura.vRazonSocialReceptor, { shouldValidate: true });
                        }
                        if (!getValues('vCP') && selectedFactura.vCPReceptor) {
                            setValue('vCP', selectedFactura.vCPReceptor, { shouldValidate: true });
                        }
                        if (!getValues('vCodigoRegimen') && selectedFactura.vCodigoRegimenReceptor) {
                            setValue('vCodigoRegimen', selectedFactura.vCodigoRegimenReceptor, { shouldValidate: true });
                            setSelectedValuesDDL(prev => ({ ...prev, vCodigoRegimen: selectedFactura.vCodigoRegimenReceptor }));
                        }
                    }
                    setShowSelectFacturaModal(false);
                }}
                title="Seleccionar factura PPD"
                rfcEmisor={rfcSeleccionado}
                rfcReceptor={getValues('vRFC') || selectedValuesDDL.vRFC}
            />
        </>
    );
};

const FormularioComplementoPago = () => {
    return (
        <ContabilidadLayout
            title={
                <><FontAwesomeIcon icon="file-invoice" className="me-2" />Generar Complemento de Pago</>
            }
        >
            <FormularioComplementoPagoContent />
        </ContabilidadLayout>
    );
};

export default FormularioComplementoPago;
